import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FileBarChart, Download, FileSpreadsheet, FileText, CalendarClock, Plus, Trash2, Pencil, X, Mail } from 'lucide-react';
import { PageHeader } from '@/components/ui/DataTable';
import { RecipientPicker } from '@/components/RecipientPicker';
import { useToast } from '@/components/ui/Toast';
import { inventoryApi, reportSchedulesApi, type ReportSchedule } from '@/lib/inventory-api';
import { ticketingApi } from '@/lib/ticketing-api';
import { useUIStore } from '@/stores/ui.store';
import { notoHebrewBase64 } from '@/lib/pdf-fonts/noto-hebrew';

// Report types the SERVER can generate for scheduled delivery (DB-derived).
const SCHEDULABLE = ['r-health', 'r-faults', 'r-contracts'];

// Each report pulls REAL data from the live endpoints and renders rows. Print
// opens the browser print dialog on the generated view; Excel downloads a CSV.
type Row = Record<string, string | number>;
interface ReportDef {
  id: string; title: string; desc: string; cadence: string;
  load: () => Promise<Row[]>;
}

const REPORTS: ReportDef[] = [
  {
    id: 'r-health', title: 'System health & compliance',
    desc: 'Per-device health score, status and failing checks.', cadence: 'Daily / on-demand',
    load: async () => (await inventoryApi.list()).map((d) => ({
      Device: d.name, Site: d.site, Type: d.type, Status: d.status, 'Health score': d.healthScore,
    })),
  },
  {
    id: 'r-faults', title: 'Faults & incidents',
    desc: 'Open incidents by device and type.', cadence: 'Weekly',
    load: async () => {
      const t = await inventoryApi.serviceTickets();
      return t.map((x) => ({ Ticket: x.ticketId, Title: x.title, Priority: x.priority, Status: x.status, Site: x.site, 'SLA breached': x.slaBreached ? 'yes' : 'no' }));
    },
  },
  {
    id: 'r-tickets', title: 'Ticket & vendor performance',
    desc: 'Vendor resolution-time and SLA ranking.', cadence: 'Monthly',
    load: async () => (await inventoryApi.vendorPerformance()).map((v) => ({
      Vendor: v.vendor, 'Avg resolution (min)': v.avgResolutionMins, 'SLA compliance %': v.slaCompliance, 'Ticket volume': v.ticketVolume,
    })),
  },
  {
    id: 'r-contracts', title: 'Contract coverage & spend',
    desc: 'Contracts, vendors and status.', cadence: 'Monthly',
    load: async () => (await ticketingApi.listContracts()).map((c) => ({
      Contract: c.contractNumber, Name: c.name, Type: c.type ?? '', Status: c.status, 'Total value': c.totalValue ?? '',
    })),
  },
  {
    id: 'r-retention', title: 'Recording-days & retention',
    desc: 'Actual vs policy retention per camera.', cadence: 'Weekly',
    load: async () => (await inventoryApi.recording()).map((r) => ({
      Device: r.deviceName, Site: r.site, Status: r.recordingStatus, 'Expected days': r.expectedRetention, 'Actual days': r.actualRetention, 'Gaps': r.gaps,
    })),
  },
];

const fileStamp = () => new Date().toISOString().slice(0, 10);

// i18next's TFunction overloads accept (key, defaultValue: string) => string, so
// type our translator to that exact positional shape (defaultValue required) — a
// looser `fallback?` signature isn't assignable from TFunction.
type TFn = (key: string, defaultValue: string) => string;

// Translate the English row-object keys → localized display headers, keeping the
// data keyed by the original English (which is how the rows are built). Returns
// the ordered original keys + their localized labels.
function localizedHeaders(rows: Row[], t: TFn): { keys: string[]; labels: string[] } {
  const keys = rows[0] ? Object.keys(rows[0]) : [];
  return { keys, labels: keys.map((k) => t(`reports.headers.${k}`, k)) };
}

// Real .xlsx via SheetJS (a worksheet from the row objects). SheetJS is UTF-8, so
// Hebrew cell values + headers store correctly; Excel renders RTL itself.
function exportXlsx(def: ReportDef, rows: Row[], t: TFn) {
  const title = t(`reports.defs.${def.id}.title`, def.title);
  const { keys, labels } = localizedHeaders(rows, t);
  // Build an array-of-arrays so we control the (localized) header row explicitly.
  const aoa = [labels, ...rows.map((r) => keys.map((k) => r[k] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31)); // sheet name ≤31 chars
  XLSX.writeFile(wb, `${def.id}-${fileStamp()}.xlsx`);
}

// Real downloadable PDF via jsPDF + autotable. jsPDF's built-in fonts have no
// Hebrew glyphs, so for `he` we register an embedded Noto Sans Hebrew subset and
// right-align the table (RTL). English keeps the default Helvetica.
function exportPdf(def: ReportDef, rows: Row[], t: TFn, lang: string) {
  const rtl = lang === 'he';
  const doc = new jsPDF({ orientation: 'landscape' });
  if (rtl) {
    doc.addFileToVFS('NotoHebrew.ttf', notoHebrewBase64);
    doc.addFont('NotoHebrew.ttf', 'NotoHebrew', 'normal');
    doc.setFont('NotoHebrew');
  }
  const title = t(`reports.defs.${def.id}.title`, def.title);
  const { keys, labels } = localizedHeaders(rows, t);
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFontSize(14);
  doc.text(title, rtl ? pageW - 14 : 14, 16, { align: rtl ? 'right' : 'left' });
  doc.setFontSize(9);
  doc.setTextColor(120);
  const meta = t('reports.generatedMeta', 'Generated {{when}} · {{n}} rows')
    .replace('{{when}}', new Date().toLocaleString(lang === 'he' ? 'he-IL' : undefined))
    .replace('{{n}}', String(rows.length));
  doc.text(meta, rtl ? pageW - 14 : 14, 22, { align: rtl ? 'right' : 'left' });
  autoTable(doc, {
    startY: 28,
    head: [labels],
    body: rows.map((r) => keys.map((k) => String(r[k] ?? ''))),
    styles: { fontSize: 8, cellPadding: 2, font: rtl ? 'NotoHebrew' : 'helvetica', halign: rtl ? 'right' : 'left' },
    headStyles: { fillColor: [56, 142, 255], font: rtl ? 'NotoHebrew' : 'helvetica', halign: rtl ? 'right' : 'left' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });
  doc.save(`${def.id}-${fileStamp()}.pdf`);
}

export function ReportsPage() {
  const { t } = useTranslation('pages');
  const language = useUIStore((s) => s.language);
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ def: ReportDef; rows: Row[] } | null>(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const [sendFor, setSendFor] = useState<ReportDef | null>(null);

  const run = async (def: ReportDef, action: 'preview' | 'excel' | 'pdf') => {
    setBusy(`${def.id}:${action}`);
    try {
      const rows = await def.load();
      if (action === 'excel') exportXlsx(def, rows, t);
      else if (action === 'pdf') exportPdf(def, rows, t, language);
      else setPreview({ def, rows });
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('reports.title')}
        subtitle={t('reports.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setSchedOpen(true)}><CalendarClock size={16} /> {t('reports.actions.scheduledReports')}</button>}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {REPORTS.map((r) => (
          <div key={r.id} className="tk-card tk-card-hover flex flex-col">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/15 text-primary"><FileBarChart size={18} /></div>
              <div className="flex-1">
                <div className="font-medium">{t(`reports.defs.${r.id}.title`, r.title)}</div>
                <div className="mt-1 text-xs text-muted">{t(`reports.defs.${r.id}.desc`, r.desc)}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted">{t('reports.cadenceLabel')} {t(`reports.defs.${r.id}.cadence`, r.cadence)}</div>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-3">
              <button className="tk-btn-ghost px-2 text-xs" disabled={!!busy} onClick={() => run(r, 'pdf')}><FileText size={14} /> {busy === `${r.id}:pdf` ? '…' : t('reports.actions.pdf')}</button>
              <button className="tk-btn-ghost px-2 text-xs" disabled={!!busy} onClick={() => run(r, 'excel')}><FileSpreadsheet size={14} /> {busy === `${r.id}:excel` ? '…' : t('reports.actions.excel')}</button>
              {SCHEDULABLE.includes(r.id) && (
                <button className="tk-btn-ghost px-2 text-xs" onClick={() => setSendFor(r)} title={t('reports.actions.sendHint')}><Mail size={14} /> {t('reports.actions.send')}</button>
              )}
              <button className="tk-btn-primary ml-auto px-3 text-xs" disabled={!!busy} onClick={() => run(r, 'preview')}><Download size={14} /> {busy === `${r.id}:preview` ? '…' : t('reports.actions.preview')}</button>
            </div>
          </div>
        ))}
      </div>

      {preview && <ReportPreview def={preview.def} rows={preview.rows} onClose={() => setPreview(null)} onPdf={() => exportPdf(preview.def, preview.rows, t, language)} onXlsx={() => exportXlsx(preview.def, preview.rows, t)} onSend={SCHEDULABLE.includes(preview.def.id) ? () => setSendFor(preview.def) : undefined} />}
      {schedOpen && <ScheduledReports reports={REPORTS} onClose={() => setSchedOpen(false)} />}
      {sendFor && <SendReportModal def={sendFor} onClose={() => setSendFor(null)} />}
    </div>
  );
}

// Manual "send now": pick a format + recipients (system users and/or typed
// emails) and email the server-generated report.
function SendReportModal({ def, onClose }: { def: ReportDef; onClose: () => void }) {
  const { t } = useTranslation('pages');
  const toast = useToast();
  const [format, setFormat] = useState<'excel' | 'pdf'>('excel');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!recipients.length) { setError(t('reports.send.noRecipients')); return; }
    setBusy(true); setError(null);
    try {
      const res = await reportSchedulesApi.send({ reportType: def.id, format, recipients });
      toast.success(t('reports.send.sent', { count: res.sent }));
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? t('reports.send.failed'));
      setBusy(false);
    }
  };

  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('reports.send.title')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('reports.send.close')}><X size={16} /></button>
        </div>
        <p className="mb-3 text-sm text-muted">{t(`reports.defs.${def.id}.title`, def.title)}</p>

        <label className="mb-1 block text-sm text-muted">{t('reports.send.format')}</label>
        <div className="mb-3 flex gap-2">
          {(['excel', 'pdf'] as const).map((f) => (
            <button
              key={f} type="button" onClick={() => setFormat(f)}
              className={`tk-btn-ghost px-3 text-xs ${format === f ? 'ring-1 ring-primary text-primary' : ''}`}
            >
              {f === 'excel' ? <FileSpreadsheet size={14} /> : <FileText size={14} />} {f === 'excel' ? t('reports.form.formatExcel') : t('reports.form.formatPdf')}
            </button>
          ))}
        </div>

        <label className="mb-1 block text-sm text-muted">{t('reports.send.recipients')}</label>
        <RecipientPicker value={recipients} onChange={setRecipients} />

        {error && <p className="mt-2 text-sm text-critical">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="tk-btn-ghost" onClick={onClose} disabled={busy}>{t('reports.send.cancel')}</button>
          <button type="button" className="tk-btn-primary" onClick={submit} disabled={busy || !recipients.length}>
            <Mail size={14} /> {busy ? t('reports.send.sending') : t('reports.send.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportPreview({ def, rows, onClose, onPdf, onXlsx, onSend }: { def: ReportDef; rows: Row[]; onClose: () => void; onPdf: () => void; onXlsx: () => void; onSend?: () => void }) {
  const { t } = useTranslation('pages');
  const headers = rows[0] ? Object.keys(rows[0]) : [];
  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal flex max-h-[85vh] w-full max-w-3xl flex-col p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t(`reports.defs.${def.id}.title`, def.title)}</h2>
          <div className="flex gap-2">
            <button className="tk-btn-ghost px-2 text-xs" onClick={onPdf}><FileText size={14} /> {t('reports.actions.pdf')}</button>
            <button className="tk-btn-ghost px-2 text-xs" onClick={onXlsx}><FileSpreadsheet size={14} /> {t('reports.actions.excel')}</button>
            {onSend && <button className="tk-btn-ghost px-2 text-xs" onClick={onSend}><Mail size={14} /> {t('reports.actions.send')}</button>}
            <button className="tk-btn-ghost px-2" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="overflow-auto">
          {rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">{t('reports.empty.noData')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-left text-xs text-muted">
                {headers.map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    {headers.map((h) => <td key={h} className="py-1.5 pr-4">{r[h]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-3 text-xs text-muted">{t('reports.preview.footer', { count: rows.length })}</div>
      </div>
    </div>
  );
}

// Scheduled reports — list + add/enable/delete. The worker generates each due
// report server-side and emails it (summary + attachment) on its cadence.
function ScheduledReports({ reports, onClose }: { reports: ReportDef[]; onClose: () => void }) {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<ReportSchedule | null>(null);
  const { data: schedules } = useQuery({ queryKey: ['report-schedules'], queryFn: reportSchedulesApi.list, retry: false });
  const schedulable = reports.filter((r) => SCHEDULABLE.includes(r.id));
  const titleOf = (id: string) => {
    const def = reports.find((r) => r.id === id);
    return def ? t(`reports.defs.${def.id}.title`, def.title) : id;
  };

  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal flex max-h-[85vh] w-full max-w-2xl flex-col p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('reports.scheduled.title')}</h2>
          <div className="flex gap-2">
            <button className="tk-btn-primary px-3 text-xs" onClick={() => setAddOpen(true)}><Plus size={14} /> {t('reports.scheduled.newSchedule')}</button>
            <button className="tk-btn-ghost px-2" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <p className="mb-3 text-xs text-muted">{t('reports.scheduled.description')}</p>
        <div className="overflow-auto">
          {(schedules ?? []).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">{t('reports.scheduled.empty')}</p>
          ) : (
            <ul className="text-sm">
              {(schedules ?? []).map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 border-b border-white/5 py-2.5 last:border-0">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{s.name}</span>
                    <span className="block truncate text-xs text-muted">{titleOf(s.reportType)} · {s.format.toUpperCase()} · {s.cadence} · {t('reports.scheduled.recipientCount', { count: s.recipients.length })}{s.lastRunAt ? ` · ${t('reports.scheduled.lastRun', { date: new Date(s.lastRunAt).toLocaleDateString() })}` : ''}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <button
                      type="button" role="switch" aria-checked={s.enabled} title={s.enabled ? t('reports.scheduled.enabled') : t('reports.scheduled.disabled')}
                      onClick={async () => { await reportSchedulesApi.update(s.id, { enabled: !s.enabled }); qc.invalidateQueries({ queryKey: ['report-schedules'] }); }}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${s.enabled ? 'bg-primary' : 'bg-white/10 ring-1 ring-white/15'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${s.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <button className="tk-btn-ghost px-1.5 py-1" title={t('reports.scheduled.edit')} onClick={() => setEditing(s)}><Pencil size={14} /></button>
                    <button className="tk-btn-ghost px-1.5 py-1" title={t('reports.scheduled.delete')} onClick={async () => { await reportSchedulesApi.remove(s.id); qc.invalidateQueries({ queryKey: ['report-schedules'] }); }}><Trash2 size={14} /></button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {addOpen && (
        <ScheduleFormModal
          mode="create" schedulable={schedulable}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => {
            await reportSchedulesApi.create({ name: v.name, reportType: v.reportType, format: v.format, cadence: v.cadence, recipients: v.recipients });
            qc.invalidateQueries({ queryKey: ['report-schedules'] });
            toast.success(t('reports.toast.scheduleCreated', { name: v.name }));
          }}
        />
      )}

      {editing && (
        <ScheduleFormModal
          mode="edit" schedulable={schedulable} initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (v) => {
            await reportSchedulesApi.update(editing.id, { name: v.name, reportType: v.reportType, format: v.format, cadence: v.cadence, recipients: v.recipients });
            qc.invalidateQueries({ queryKey: ['report-schedules'] });
            toast.success(t('reports.toast.scheduleSaved', { name: v.name }));
          }}
        />
      )}
    </div>
  );
}

// Add/edit a scheduled report. Uses the shared RecipientPicker so scheduling
// gets the same system-user + free-text recipient selection as manual send.
interface ScheduleFormValues { name: string; reportType: string; format: 'pdf' | 'excel'; cadence: 'daily' | 'weekly' | 'monthly'; recipients: string[] }
function ScheduleFormModal({
  mode, schedulable, initial, onClose, onSubmit,
}: {
  mode: 'create' | 'edit';
  schedulable: ReportDef[];
  initial?: ReportSchedule;
  onClose: () => void;
  onSubmit: (v: ScheduleFormValues) => Promise<void>;
}) {
  const { t } = useTranslation('pages');
  const [name, setName] = useState(initial?.name ?? '');
  const [reportType, setReportType] = useState(initial?.reportType ?? schedulable[0]?.id ?? '');
  const [format, setFormat] = useState<'pdf' | 'excel'>(initial?.format ?? 'excel');
  const [cadence, setCadence] = useState<'daily' | 'weekly' | 'monthly'>(initial?.cadence ?? 'weekly');
  const [recipients, setRecipients] = useState<string[]>(initial?.recipients ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) { setError(t('reports.form.errors.name')); return; }
    if (!recipients.length) { setError(t('reports.send.noRecipients')); return; }
    setBusy(true); setError(null);
    try {
      await onSubmit({ name: name.trim(), reportType, format, cadence, recipients });
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? t('reports.send.failed'));
      setBusy(false);
    }
  };

  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal flex max-h-[85vh] w-full max-w-md flex-col overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{mode === 'create' ? t('reports.form.title') : t('reports.form.editTitle')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('reports.send.close')}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm text-muted">{t('reports.form.name')}<span className="text-critical"> *</span></label>
            <input className="tk-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('reports.form.namePlaceholder')} autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-sm text-muted">{t('reports.form.report')}</label>
            <select className="tk-input" value={reportType} onChange={(e) => setReportType(e.target.value)}>
              {schedulable.map((r) => <option key={r.id} value={r.id}>{t(`reports.defs.${r.id}.title`, r.title)}</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-sm text-muted">{t('reports.form.format')}</label>
              <select className="tk-input" value={format} onChange={(e) => setFormat(e.target.value as 'pdf' | 'excel')}>
                <option value="excel">{t('reports.form.formatExcel')}</option>
                <option value="pdf">{t('reports.form.formatPdf')}</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-sm text-muted">{t('reports.form.cadence')}</label>
              <select className="tk-input" value={cadence} onChange={(e) => setCadence(e.target.value as 'daily' | 'weekly' | 'monthly')}>
                <option value="daily">{t('reports.form.cadenceDaily')}</option>
                <option value="weekly">{t('reports.form.cadenceWeekly')}</option>
                <option value="monthly">{t('reports.form.cadenceMonthly')}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm text-muted">{t('reports.form.recipients')}<span className="text-critical"> *</span></label>
            <RecipientPicker value={recipients} onChange={setRecipients} />
          </div>
        </div>
        {error && <p className="mt-2 text-sm text-critical">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="tk-btn-ghost" onClick={onClose} disabled={busy}>{t('reports.send.cancel')}</button>
          <button type="button" className="tk-btn-primary" onClick={submit} disabled={busy || !name.trim() || !recipients.length}>
            {busy ? t('reports.send.sending') : (mode === 'create' ? t('reports.form.submit') : t('reports.form.save'))}
          </button>
        </div>
      </div>
    </div>
  );
}
